import React, { type ComponentProps, forwardRef, useState } from 'react'
// Libs
import { twMerge } from 'tailwind-merge'
import { tv, type VariantProps } from 'tailwind-variants'

const textareaVariants = tv({
  base: 'absolute text-base duration-300 text-text-disabled group-hover:text-text-primary group-enabled:group-hover:cursor-pointer group-data-[active=true]:text-text-primary group-data-[error=true]:group-data-[active=true]:text-error group-data-[disabled=true]:text-borderColor/30',
  variants: {
    variant: {
      form: 'group-data-[active=true]:mt-[-2.375rem] group-data-[active=true]:ml-[-0.75rem] group-data-[active=true]:text-sm',
      filter:
        'block px-1 bg-white group-data-[active=true]:mt-[-1.265rem] group-data-[active=true]:text-xs group-data-[active=true]:ml-[-0.25rem]',
    },
  },
  defaultVariants: {
    variant: 'filter',
  },
})

type TextareaVariantions = VariantProps<typeof textareaVariants>

type TextareaProps = TextareaVariantions &
  ComponentProps<'textarea'> & {
    label?: string
    errorMessage?: string | null
    onValueChange: (e: string) => void
  }

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { label, variant, className, errorMessage = null, ...props },
    ref,
  ): JSX.Element {
    delete props.onFocus
    delete props.onBlur

    const [isFocusedOrIsFilled, setIsFocusOrIsFilled] = useState(!!props.value)

    const hasError = !!errorMessage
    const isDisabled = props.disabled
    const placeholder = isFocusedOrIsFilled ? props.placeholder : ''

    function handleTextareaFocus(): void {
      setIsFocusOrIsFilled(true)
    }

    function handleTextareaBlur(
      e: React.ChangeEvent<HTMLTextAreaElement>,
    ): void {
      if (!e.target.value) {
        setIsFocusOrIsFilled(false)
      }
    }

    return (
      <div className={twMerge('w-full space-y-1.5', className)}>
        <div className="flex flex-col w-full relative">
          <div
            data-active={isFocusedOrIsFilled}
            data-error={hasError}
            data-disabled={isDisabled}
            className="
          group mt-auto p-3 h-[9.063rem] w-full flex items-start justify-start border border-text-disabled rounded-[0.375rem] transition-all
          data-[disabled=false]:hover:border-text-primary enabled:hover:cursor-pointer
          data-[disabled=true]:border-borderColor/30
          data-[error=true]:border-error
          focus-within:border-text-primary"
          >
            {label && (
              <label className={textareaVariants({ variant })}>{label}</label>
            )}
            <textarea
              ref={ref}
              data-disabled={isDisabled}
              className="
            h-full data-[disabled=true]:text-borderColor/30 w-full z-10 border-none bg-transparent leading-6 text-text-primary resize-none
            focus:outline-none pr-3"
              onFocus={handleTextareaFocus}
              onBlur={handleTextareaBlur}
              placeholder={placeholder}
              onChange={e => props.onValueChange(e.target.value)}
              {...props}
            />
          </div>
        </div>

        {hasError && (
          <span className="block text-error text-sm">{errorMessage}</span>
        )}
      </div>
    )
  },
)
